/**
 * Carrier MCP — rate_limit_status tool (CAR-39 wave-2).
 *
 * Exposes the in-process rate-governor window state so LLMs and operators
 * can inspect current Bridge4IP/OCS API quota consumption without a live call.
 *
 * Read-only. Requires "read" scope. No OCS call — in-memory state read.
 */

import { z } from "zod";
import type { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { wrapHandler, type ToolContext } from "./tools.js";
import { getLimitForEndpoint, getRateLimitWindowCounts } from "@carrier/ocs-client";

// The set of endpoints worth surfacing in a status call (most-constrained first).
const MONITORED_ENDPOINTS = [
  "subscriberNetworkEventsOverPeriod", // 30/min
  "subscriberUsageOverPeriod",          // 100/min
  "listLocationZoneElement",            // 150/min
  "sendMtSms",                          // 150/min
  "modifySubscriberMobilePlan",         // 300/min
  "affectRecurringPackageToSubscriber", // 300/min
  "listSubscriber",                     // 300/min
  "listSubscriberPrepaidPackages",      // 300/min
  "getSingleSubscriber",                // 300/min
  "getSubscriberLocationByCellId",      // 300/min
  "__global__",                         // 600/min
] as const;

export function registerRateLimitStatusTool(
  server: McpServer,
  ctx: ToolContext,
): void {
  server.registerTool(
    "rate_limit_status",
    {
      title: "API Rate-Limit Governor Status",
      description:
        "Inspect Bridge4IP OCS rate-limit governor state — bucket fill levels, " +
        "per-endpoint limits, and 80% alert thresholds. " +
        "Use this to answer: 'are we rate limited', 'check ocs quota', " +
        "'how full is the rate limit bucket', 'rate limit status'. " +
        "Params: `endpoint` (optional — OCS camelCase method name to inspect a single bucket, " +
        "e.g. 'subscriberUsageOverPeriod'; omit for all monitored endpoints). " +
        "Returns: per-endpoint { limit_per_min, batch_cap_per_min, calls_in_window, " +
        "batch_calls_in_window, utilisation_pct, alert (true when ≥80% of limit) }. " +
        "Note: counts come from the in-process sliding-window log (same key as OCS calls: API token + endpoint) — " +
        "they reset when the Worker isolate recycles. Use as a relative signal, not an exact counter. " +
        "Do NOT use this to adjust limits — limits are set by Bridge4IP contract and cannot be changed via MCP.",
      inputSchema: {
        endpoint: z
          .string()
          .optional()
          .describe(
            "OCS method name in camelCase (e.g. 'subscriberUsageOverPeriod'). " +
            "Omit to return status for all monitored endpoints.",
          ),
      },
      annotations: { readOnlyHint: true, idempotentHint: true },
    },
    wrapHandler(
      "rate_limit_status",
      "[rate-governor:in-process]",
      "read",
      ctx,
      async ({ endpoint }: { endpoint?: string }, token: string) => {
        const targets = endpoint
          ? [endpoint]
          : (MONITORED_ENDPOINTS as readonly string[]);

        const rows = targets.map((ep) => {
          const limit = getLimitForEndpoint(ep);
          const batch_cap_per_min = Math.floor(limit * 0.2);
          const { calls_in_window, batch_calls_in_window } =
            getRateLimitWindowCounts(token, ep);
          const utilisation_pct =
            limit > 0 ? Math.round((calls_in_window / limit) * 100) : 0;
          const alert = utilisation_pct >= 80;
          return {
            endpoint: ep,
            limit_per_min: limit,
            batch_cap_per_min,
            calls_in_window,
            batch_calls_in_window,
            utilisation_pct,
            alert,
          };
        });

        const resellerId = String(ctx.props.reseller_id);

        return {
          content: [
            {
              type: "text" as const,
              text: JSON.stringify(
                {
                  reseller_id: resellerId,
                  window_seconds: 60,
                  batch_fraction: 0.2,
                  endpoints: rows,
                  alert_threshold_pct: 80,
                  metric_name: "carrier_ocs_calls_per_min",
                  metric_location:
                    "CF Workers Tail / BetterStack — structured JSON log lines with metric='carrier_ocs_calls_per_min'",
                },
                null,
                2,
              ),
            },
          ],
        };
      },
    ),
  );
}
